import type { FilterOperator, FilterOperatorSlot, PrimitiveType } from '#layers/data-model/public';
import { getPrimitiveFieldPresentationDefaults } from '#layers/data-model/public';

export type SupportedOperatorsSpec =
	| FilterOperator[]
	| ((operators: FilterOperator[]) => FilterOperator[]);

export interface OperatorResolutionOptions {
	mode?: 'default' | 'advanced';
	/** An operator a filter already uses; it stays offered while the field accepts it. */
	retain?: FilterOperator;
}

export function getSupportedOperators(type: PrimitiveType): FilterOperator[] {
	return getPrimitiveFieldPresentationDefaults(type).filter?.supportedOperators ?? [];
}

export function getDefaultOperators(type: PrimitiveType): FilterOperatorSlot[] {
	const filter = getPrimitiveFieldPresentationDefaults(type).filter;
	return filter?.defaultOperators ?? (filter?.supportedOperators ?? []).map((operator) => [operator]);
}

export function narrowOperators(
	baseOperators: FilterOperator[],
	spec?: SupportedOperatorsSpec,
): FilterOperator[] {
	if (!spec) return baseOperators;
	if (Array.isArray(spec)) return baseOperators.filter((op) => spec.includes(op));
	return spec(baseOperators);
}

export function getNarrowedOperatorsForField(
	field: { type: PrimitiveType } | undefined,
	spec: SupportedOperatorsSpec | undefined,
	options: OperatorResolutionOptions = {},
): FilterOperator[] {
	if (!field) return [];
	const accepted = narrowOperators(getSupportedOperators(field.type), spec);
	if (options.mode === 'advanced') return accepted;

	// The usual menu is a preference, not a capability limit: each entry offers the first operator
	// the field accepts, and a contract that accepts none of them offers what it does accept.
	const preferred = getDefaultOperators(field.type).flatMap((slot) => slot.find((operator) => accepted.includes(operator)) ?? []);
	const operators = preferred.length > 0 ? preferred : accepted;
	const { retain } = options;
	return retain && accepted.includes(retain) && !operators.includes(retain) ? [...operators, retain] : operators;
}

/**
 * Picks the seeded default operator from a narrowed list. Honors an explicit `defaultOperator`
 * only when it is present in `operators` (defensive against config/operator drift); otherwise
 * returns the first operator, or `undefined` when no operator is available.
 */
export function resolveDefaultOperator(
	operators: FilterOperator[],
	defaultOperator?: FilterOperator,
): FilterOperator | undefined {
	if (defaultOperator && operators.includes(defaultOperator)) return defaultOperator;
	return operators[0];
}

export function getDefaultOperatorForField(
	field: { type: PrimitiveType } | undefined,
	spec: SupportedOperatorsSpec | undefined,
	defaultOperator?: FilterOperator,
): FilterOperator | undefined {
	return resolveDefaultOperator(getNarrowedOperatorsForField(field, spec), defaultOperator);
}
